import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getLeavesByEmployeeId,
  getLeaveBalanceFromUserAndMeta,
} from "../utils";
import { Button } from "../components";
import { useNavigate } from "react-router-dom";
import { strings } from "../utils";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CalenderIcon from "../components/Icons/calender";
import ApproveIcon from "../components/Icons/approveIcon";
import CloseIcon from "../components/Icons/closeIcon";

const LeaveRequest = () => {
  const [leaves, setLeaves] = useState([]);
  const [userId, setUserId] = useState(null);
  const [leavesData, setLeavesData] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [carryForward, setCarryForward] = useState({});
  const [showLeaveRequests, setShowLeaveRequests] = useState(true);
  const [showRemainingLeaves, setShowRemainingLeaves] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        fetchLeavesAndBalance(user.uid);
      } else {
        console.error("User is not authenticated.");
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchLeavesAndBalance = async (userId) => {
    try {
      const { leavesData, leaveBalance } = await getLeaveBalanceFromUserAndMeta(
        userId
      );

      const fetchedLeaves = await getLeavesByEmployeeId(userId);

      const sortedFetchedLeaves = fetchedLeaves.sort((a, b) => {
        const timestampA = a.createdAt.seconds;
        const timestampB = b.createdAt.seconds;
        return timestampB - timestampA;
      });

      const carryForwardData = sortedFetchedLeaves.reduce((acc, leave) => {
        if (leave.status !== "Approved" && leave.status !== "Rejected") {
          acc[leave.leaveType] =
            (acc[leave.leaveType] || 0) +
            calculateLeaveDays(leave.leaveStartingDate, leave.leaveEndingDate);
        }
        return acc;
      }, {});

      const totalLeaves = Object.values(carryForwardData).reduce(
        (sum, leaveDays) => sum + leaveDays,
        0
      );
      carryForwardData["Total Leaves"] = totalLeaves;

      setLeaves(sortedFetchedLeaves);
      setLeavesData(leavesData);
      setLeaveBalance(leaveBalance);
      setCarryForward(carryForwardData);
    } catch (error) {
      console.error("Error fetching leaves and balance:", error);
    }
  };

  const calculateLeaveDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const calculateAvailedLeaves = (total, remaining) => {
    return total - remaining;
  };

  const handleAddRequest = () => {
    navigate(`/leaveRequest/form/new`);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Approved":
        return "bg-color-17 text-primary text-sm font-semibold";
      case "Rejected":
        return "bg-color-18 text-color-12 text-sm font-semibold";
      default:
        return "bg-border text-color-1 text-sm font-semibold";
    }
  };

  const handleShowLeaveRequests = () => {
    setShowLeaveRequests(true);
    setShowRemainingLeaves(false);
  };

  const handleShowRemainingLeaves = () => {
    setShowLeaveRequests(false);
    setShowRemainingLeaves(true);
  };

  return (
    <div className="container px-10 py-6 h-full bg-border overflow-scroll">
      <div className="flex justify-between mb-8">
        <p className="text-[32px] font-semibold">{strings["empLeaveTitle"]}</p>
        <Button onClick={handleAddRequest}>{strings["applyLeaveBtn"]}</Button>
      </div>
      <div className="flex items-center gap-x-2">
        <Button
          variant="outlineArrow"
          classes={`hover:bg-primary hover:text-white ${
            showLeaveRequests ? "bg-primary text-white border-primary" : ""
          }`}
          onClick={handleShowLeaveRequests}
        >
          {strings["appliedLeaveBtn"]}
        </Button>
        <Button
          variant="outlineArrow"
          classes={`hover:bg-primary hover:text-white ${
            showRemainingLeaves ? "bg-primary text-white border-primary" : ""
          }`}
          onClick={handleShowRemainingLeaves}
        >
          {strings["remainingLeavesBtn"]}
        </Button>
      </div>

      <div className="py-6">
        {showLeaveRequests &&
          (leaves.length === 0 ? (
            <p>No leave requests found.</p>
          ) : (
            leaves.map((leave) => (
              <div
                key={leave.id}
                className="flex justify-between items-center px-6 py-3 bg-white rounded-md mb-4"
              >
                <div>
                  <p className="text-lg font-semibold">
                    {leave.leaveType || "Leave Type"}
                  </p>
                  <div className="flex items-center gap-x-1">
                    <CalenderIcon />
                    <p className="text-sm font-medium text-color-1">
                      No of days:{" "}
                      {calculateLeaveDays(
                        leave.leaveStartingDate,
                        leave.leaveEndingDate
                      ) || "0"}
                    </p>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-x-2 p-[10px] rounded-md ${getStatusStyles(
                    leave.status || "Waiting for Manager Approval"
                  )}`}
                >
                  {leave.status === "Approved" && <ApproveIcon />}
                  {leave.status === "Rejected" && <CloseIcon />}
                  <p>{leave.status || "Waiting for Manager Approval"}</p>
                </div>
              </div>
            ))
          ))}
      </div>

      {showRemainingLeaves && leavesData && leaveBalance && (
        <TableContainer
          component={Paper}
          sx={{ border: "2px solid border", borderRadius: "16px" }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ backgroundColor: "#5D6B7F" }}>
              <TableRow>
                <TableCell sx={{ color: "#FFF" }} align="center">
                  Category
                </TableCell>
                <TableCell sx={{ color: "#FFF" }} align="center">
                  Carry Forward
                </TableCell>
                <TableCell sx={{ color: "#FFF" }} align="center">
                  Total
                </TableCell>
                <TableCell sx={{ color: "#FFF" }} align="center">
                  Availed
                </TableCell>
                <TableCell sx={{ color: "#FFF" }} align="center">
                  Remaining
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {["al", "sl", "cl", "totalLeaves"].map((leaveType, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    {leaveType === "al"
                      ? "Annual Leave"
                      : leaveType === "sl"
                      ? "Sick Leave"
                      : leaveType === "cl"
                      ? "Casual Leave"
                      : "Total Leaves"}
                  </TableCell>
                  <TableCell align="center">
                    {leaveType === "sl"
                      ? carryForward["Sick Leave"] || "0"
                      : leaveType === "al"
                      ? carryForward["Annual Leave"] || "0"
                      : leaveType === "cl"
                      ? carryForward["Casual Leave"] || "0"
                      : carryForward["Total Leaves"] || "0"}
                  </TableCell>

                  <TableCell align="center">{leavesData[leaveType]}</TableCell>
                  <TableCell align="center">
                    {calculateAvailedLeaves(
                      leavesData[leaveType],
                      leaveBalance[leaveType]
                    ) || "0"}
                  </TableCell>
                  <TableCell align="center">
                    {leaveBalance[leaveType]}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default LeaveRequest;
